import { useState, useEffect } from 'react';
import { Bell, Check, CheckCheck, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../config/axiosConfig';

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
  }, [filter]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const params = filter === 'unread' ? '?unread_only=true' : '';
      const response = await axiosInstance.get(`/notifications${params}`);
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axiosInstance.patch(`/notifications/${notificationId}/mark-read`);
      fetchNotifications(); // Refresh list
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axiosInstance.patch('/notifications/mark-all-read');
      fetchNotifications(); // Refresh list
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.is_read) {
      markAsRead(notification.id);
    }

    // Navigate based on event type
    if (notification.jd_id) {
      navigate(`/jd/${notification.jd_id}`);
    } else if (notification.candidate_id) {
      navigate(`/candidates`);
    }
  };

  const getNotificationColor = (eventType) => {
    switch (eventType) {
      case 'JD_MATCH':
        return 'border-teal/40 bg-teal/10';
      case 'FOLLOW_UP':
        return 'border-gold/40 bg-gold/10';
      case 'JD_ASSIGNMENT':
        return 'border-blue-400/40 bg-blue-400/10';
      default:
        return 'border-textLight/20 bg-navy/20';
    }
  };

  const getEventTypeLabel = (eventType) => {
    switch (eventType) {
      case 'JD_MATCH':
        return { label: 'Match', color: 'text-teal bg-teal/20 border-teal/30' };
      case 'FOLLOW_UP':
        return { label: 'Follow-Up', color: 'text-gold bg-gold/20 border-gold/30' };
      case 'JD_ASSIGNMENT':
        return { label: 'Assignment', color: 'text-blue-400 bg-blue-400/20 border-blue-400/30' };
      default:
        return { label: 'General', color: 'text-textLight bg-navy/20 border-textLight/30' };
    }
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const created = new Date(timestamp);
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return created.toLocaleDateString();
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread') return !n.is_read;
    if (filter === 'read') return n.is_read;
    return true;
  });

  return (
    <div className="min-h-screen bg-navy p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-navy-light border border-teal/30 rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Bell className="w-8 h-8 text-gold" />
              <div>
                <h1 className="text-3xl font-heading font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal to-gold">
                  Notifications
                </h1>
                <p className="text-textLight/60 text-sm mt-1">
                  {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center space-x-2 bg-teal text-white px-4 py-2 rounded-lg hover:bg-teal-dark transition-colors"
              >
                <CheckCheck className="w-4 h-4" />
                <span>Mark All Read</span>
              </button>
            )}
          </div>

          {/* Filters */}
          <div className="flex space-x-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-teal text-white'
                  : 'bg-navy border border-teal/30 text-textLight/70 hover:border-teal'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'unread'
                  ? 'bg-teal text-white'
                  : 'bg-navy border border-teal/30 text-textLight/70 hover:border-teal'
              }`}
            >
              Unread ({unreadCount})
            </button>
            <button
              onClick={() => setFilter('read')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'read'
                  ? 'bg-teal text-white'
                  : 'bg-navy border border-teal/30 text-textLight/70 hover:border-teal'
              }`}
            >
              Read
            </button>
          </div>
        </div>

        {/* Notifications List */}
        {loading ? (
          <div className="bg-navy-light border border-teal/30 rounded-lg shadow-lg p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal border-t-transparent mx-auto mb-4"></div>
            <p className="text-textLight/60">Loading notifications...</p>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="bg-navy-light border border-teal/30 rounded-lg shadow-lg p-12 text-center">
            <Bell className="w-16 h-16 text-textLight/30 mx-auto mb-4" />
            <h3 className="text-xl font-heading text-textLight mb-2">No notifications</h3>
            <p className="text-textLight/60">
              {filter === 'unread' 
                ? "You're all caught up! No unread notifications."
                : filter === 'read'
                ? "No read notifications yet."
                : "You haven't received any notifications yet."}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredNotifications.map((notification) => {
              const eventType = getEventTypeLabel(notification.event_type);
              
              return (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`bg-navy-light border rounded-lg shadow-lg p-6 cursor-pointer transition-all hover:shadow-xl hover:border-teal ${
                    !notification.is_read ? 'border-teal/50' : 'border-textLight/20'
                  } ${getNotificationColor(notification.event_type)}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${eventType.color}`}>
                          {eventType.label}
                        </span>
                        <span className="text-xs text-textLight/50">
                          {getTimeAgo(notification.created_at)}
                        </span>
                        {!notification.is_read && (
                          <span className="flex items-center space-x-1 text-xs text-teal">
                            <div className="w-2 h-2 bg-teal rounded-full"></div>
                            <span>New</span>
                          </span>
                        )}
                      </div>

                      <p className="text-textLight leading-relaxed">
                        {notification.message}
                      </p>
                    </div>

                    {!notification.is_read && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsRead(notification.id);
                        }}
                        className="ml-4 p-2 text-textLight/50 hover:text-teal hover:bg-teal/10 rounded-lg transition-colors"
                        title="Mark as read"
                      >
                        <Check className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
